import { useEffect, useState } from "react";
import api, { formatAxiosError } from "@/lib/api";
import { BACKEND_URL } from "@/lib/config";
import { useDialog } from "@/context/DialogContext";
import {
  Plus, PencilSimple, Trash, CheckCircle, XCircle, Copy,
  Cloud, ArrowClockwise, TestTube, Info,
} from "@phosphor-icons/react";

const DEFAULT_CFG = {
  issuer_url: "",
  client_id: "",
  client_secret: "",
  scopes: ["openid", "email", "profile"],
  allowed_domains: [],
  default_role: "employee",
};

export default function SSOSettingsTab() {
  const { confirm } = useDialog();
  const [providers, setProviders] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState({ type: "", text: "" });
  const [editing, setEditing] = useState(null); // provider object or {new: true, template: {...}}
  const [testResult, setTestResult] = useState({});

  const flash = (type, text) => {
    setMsg({ type, text });
    setTimeout(() => setMsg({ type: "", text: "" }), 5000);
  };

  const load = async () => {
    setLoading(true);
    try {
      const [pr, tpl] = await Promise.all([
        api.get("/sso/providers"),
        api.get("/sso/templates"),
      ]);
      setProviders(pr.data || []);
      setTemplates(tpl.data || []);
    } catch (e) {
      flash("error", formatAxiosError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const startCreate = (template) => {
    setEditing({
      _new: true,
      key: template.id === "custom" ? "" : template.id,
      kind: template.kind || "oidc",
      label: template.label,
      icon: template.icon || "",
      enabled: false,
      config: { ...DEFAULT_CFG, issuer_url: template.issuer_url || "" },
      _template_notes: template.notes,
    });
  };

  const startEdit = (p) => {
    setEditing({
      ...p,
      config: { ...DEFAULT_CFG, ...(p.config || {}) },
      // Blank secret so admin sees "leave blank to keep unchanged"
      _existing_secret: (p.config || {}).client_secret,
    });
  };

  const save = async () => {
    if (!editing) return;
    const isNew = editing._new;
    // Guard: SAML placeholder
    if (editing.kind === "saml") {
      flash("error", "SAML 2.0 support is coming soon. Please use OIDC for now.");
      return;
    }
    const cfg = { ...editing.config };
    if (!isNew && (cfg.client_secret === "" || cfg.client_secret === "***")) {
      delete cfg.client_secret; // keep old one on the server
    }
    // Split textarea lists
    const scopesArr = Array.isArray(cfg.scopes) ? cfg.scopes : (cfg.scopes || "").toString().split(/\s+/).filter(Boolean);
    const domainsArr = Array.isArray(cfg.allowed_domains) ? cfg.allowed_domains : (cfg.allowed_domains || "").toString().split(/[\s,]+/).filter(Boolean);
    cfg.scopes = scopesArr;
    cfg.allowed_domains = domainsArr;
    try {
      if (isNew) {
        await api.post("/sso/providers", {
          key: editing.key,
          kind: editing.kind || "oidc",
          label: editing.label,
          icon: editing.icon || "",
          enabled: !!editing.enabled,
          config: cfg,
        });
        flash("ok", "Provider created.");
      } else {
        await api.patch(`/sso/providers/${editing.id}`, {
          label: editing.label,
          icon: editing.icon || "",
          enabled: !!editing.enabled,
          config: cfg,
        });
        flash("ok", "Provider updated.");
      }
      setEditing(null);
      await load();
    } catch (e) {
      flash("error", formatAxiosError(e));
    }
  };

  const del = async (p) => {
    const ok = await confirm({
      title: `Delete SSO provider "${p.label}"?`,
      description: "Users who authenticate through this provider will no longer be able to sign in via SSO.",
      confirmText: "Delete provider",
      variant: "destructive",
    });
    if (!ok) return;
    try {
      await api.delete(`/sso/providers/${p.id}`);
      flash("ok", "Provider deleted.");
      await load();
    } catch (e) {
      flash("error", formatAxiosError(e));
    }
  };

  const toggle = async (p) => {
    try {
      await api.patch(`/sso/providers/${p.id}`, { enabled: !p.enabled });
      await load();
    } catch (e) {
      flash("error", formatAxiosError(e));
    }
  };

  const runTest = async (p) => {
    setTestResult({ ...testResult, [p.id]: { loading: true } });
    try {
      const r = await api.post(`/sso/providers/${p.id}/test`);
      setTestResult({ ...testResult, [p.id]: r.data });
    } catch (e) {
      setTestResult({ ...testResult, [p.id]: { ok: false, error: formatAxiosError(e) } });
    }
  };

  const callbackUrl = (key) => `${BACKEND_URL}/api/sso/${key || "<provider-key>"}/callback`;

  const copy = (text) => {
    try {
      navigator.clipboard.writeText(text);
      flash("ok", "Copied to clipboard.");
    } catch { /* ignore */ }
  };

  return (
    <div className="max-w-5xl space-y-8">
      {msg.text && (
        <div className={`text-xs font-mono-mini uppercase tracking-widest px-3 py-2 border ${msg.type === "ok" ? "border-green-500 text-green-700 bg-green-50" : "border-red-500 text-red-700 bg-red-50"}`}>
          {msg.text}
        </div>
      )}

      {/* Info banner */}
      <div className="border border-border p-4 flex items-start gap-3 bg-muted/30">
        <Info size={18} weight="bold" className="text-[#002FA7] mt-0.5" />
        <div className="text-sm">
          <div className="font-semibold mb-1">Pluggable Single Sign-On</div>
          <p className="text-muted-foreground text-xs">
            Configure any number of OpenID Connect (OIDC) providers below. Enabled providers appear
            as buttons on the Login page. Google, Microsoft/Azure AD, Okta and Auth0 are all OIDC —
            pick a template to auto-fill the issuer URL, then paste your client ID + secret.
            SAML 2.0 is coming in a future release.
          </p>
        </div>
      </div>

      {/* Existing providers */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display text-xl font-black">SSO Providers</h3>
          <button onClick={load} className="border border-border px-3 py-1 text-xs hover:bg-muted flex items-center gap-1">
            <ArrowClockwise size={12} weight="bold" /> Refresh
          </button>
        </div>
        {loading && <div className="text-sm text-muted-foreground">Loading…</div>}
        {!loading && providers.length === 0 && (
          <div className="border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
            No providers configured yet. Pick a template below to add one.
          </div>
        )}
        <div className="space-y-2">
          {providers.map(p => (
            <div key={p.id} className="border border-border p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3">
                  <div className="text-2xl leading-none pt-0.5">{p.icon || "🔹"}</div>
                  <div>
                    <div className="font-display text-lg font-black flex items-center gap-2">
                      {p.label}
                      <span className={`text-[10px] font-mono-mini px-1.5 py-0.5 border uppercase ${p.enabled ? "border-green-500 text-green-700 bg-green-50" : "border-muted-foreground text-muted-foreground bg-muted"}`}>
                        {p.enabled ? "enabled" : "disabled"}
                      </span>
                      <span className="text-[10px] font-mono-mini uppercase text-muted-foreground">{p.kind}</span>
                    </div>
                    <div className="text-xs text-muted-foreground font-mono-mini">key: {p.key}</div>
                    <div className="text-xs text-muted-foreground mt-1">
                      issuer: <span className="font-mono-mini">{p.config?.issuer_url || "—"}</span>
                    </div>
                    {p.config?.allowed_domains?.length > 0 && (
                      <div className="text-xs text-muted-foreground mt-0.5">
                        allowed domains: {p.config.allowed_domains.join(", ")}
                      </div>
                    )}
                    <div className="text-xs text-muted-foreground mt-1 flex items-center gap-2">
                      Callback URL:
                      <code className="bg-muted px-1.5 py-0.5 text-[10px]">{callbackUrl(p.key)}</code>
                      <button onClick={() => copy(callbackUrl(p.key))} className="text-[#002FA7] hover:underline flex items-center gap-0.5 text-[10px]">
                        <Copy size={10} /> Copy
                      </button>
                    </div>
                    {testResult[p.id] && (
                      <div className={`text-[10px] font-mono-mini mt-2 px-2 py-1 border ${testResult[p.id].ok ? "border-green-500 text-green-700 bg-green-50" : "border-red-500 text-red-700 bg-red-50"}`}>
                        {testResult[p.id].loading ? "Testing…" : (testResult[p.id].ok ? `✓ Discovery OK — auth: ${testResult[p.id].authorization_endpoint}` : `✗ ${testResult[p.id].error}`)}
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button onClick={() => toggle(p)} className="border border-border px-2 py-1 text-xs hover:bg-muted flex items-center gap-1" title={p.enabled ? "Disable" : "Enable"} data-testid={`sso-toggle-${p.key}`}>
                    {p.enabled ? <XCircle size={12} weight="bold" /> : <CheckCircle size={12} weight="bold" />}
                    {p.enabled ? "Disable" : "Enable"}
                  </button>
                  <button onClick={() => runTest(p)} className="border border-border px-2 py-1 text-xs hover:bg-muted flex items-center gap-1" data-testid={`sso-test-${p.key}`}>
                    <TestTube size={12} weight="bold" /> Test
                  </button>
                  <button onClick={() => startEdit(p)} className="border border-border px-2 py-1 text-xs hover:bg-muted flex items-center gap-1" data-testid={`sso-edit-${p.key}`}>
                    <PencilSimple size={12} weight="bold" /> Edit
                  </button>
                  <button onClick={() => del(p)} className="border border-red-500 text-red-700 px-2 py-1 text-xs hover:bg-red-50 flex items-center gap-1">
                    <Trash size={12} weight="bold" /> Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Template picker */}
      <section>
        <h3 className="font-display text-xl font-black mb-3">Add a new provider</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
          {templates.map(t => (
            <button
              key={t.id}
              onClick={() => startCreate(t)}
              disabled={t.kind === "saml"}
              className="border border-border p-3 text-left hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid={`sso-template-${t.id}`}
            >
              <div className="flex items-center gap-2">
                <div className="text-xl">{t.icon}</div>
                <div className="font-display font-black">{t.label}</div>
              </div>
              <div className="text-[10px] font-mono-mini uppercase mt-1 text-muted-foreground">{t.kind}</div>
            </button>
          ))}
        </div>
      </section>

      {/* Modal editor */}
      {editing && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <div className="bg-white border border-border max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="border-b border-border p-4 flex items-center justify-between">
              <div>
                <div className="font-display text-xl font-black flex items-center gap-2">
                  <Cloud size={18} weight="bold" />
                  {editing._new ? `New ${editing.label}` : `Edit ${editing.label}`}
                </div>
                <div className="text-xs text-muted-foreground font-mono-mini">{editing.kind?.toUpperCase()}</div>
              </div>
              <button onClick={() => setEditing(null)} className="text-2xl leading-none">×</button>
            </div>
            <div className="p-4 space-y-4">
              {editing._template_notes && (
                <div className="text-xs border-l-4 border-l-[#002FA7] bg-blue-50 p-3 text-blue-900">{editing._template_notes}</div>
              )}
              {/* Callback URL prominent */}
              <div className="border border-border p-3">
                <div className="text-[10px] font-mono-mini uppercase text-muted-foreground mb-1">Callback URL (register this at your IdP)</div>
                <div className="flex items-center gap-2">
                  <code className="flex-1 bg-muted px-2 py-1 text-[11px] break-all">{callbackUrl(editing.key)}</code>
                  <button onClick={() => copy(callbackUrl(editing.key))} className="border border-border px-2 py-1 text-xs flex items-center gap-1 hover:bg-muted">
                    <Copy size={12} /> Copy
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="eyebrow block mb-1">Key (slug) *</label>
                  <input
                    type="text"
                    value={editing.key || ""}
                    disabled={!editing._new}
                    onChange={(e) => setEditing({ ...editing, key: e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, "") })}
                    className="w-full border border-border px-3 py-2 text-sm disabled:bg-muted"
                    placeholder="e.g. google"
                    data-testid="sso-edit-key"
                  />
                </div>
                <div>
                  <label className="eyebrow block mb-1">Label *</label>
                  <input
                    type="text"
                    value={editing.label || ""}
                    onChange={(e) => setEditing({ ...editing, label: e.target.value })}
                    className="w-full border border-border px-3 py-2 text-sm"
                    placeholder="Shown on the login page"
                  />
                </div>
              </div>

              <div>
                <label className="eyebrow block mb-1">Icon (emoji or 1-2 chars)</label>
                <input
                  type="text"
                  value={editing.icon || ""}
                  onChange={(e) => setEditing({ ...editing, icon: e.target.value })}
                  className="w-24 border border-border px-3 py-2 text-sm"
                  placeholder="🔵"
                />
              </div>

              <div>
                <label className="eyebrow block mb-1">Issuer URL *</label>
                <input
                  type="text"
                  value={editing.config?.issuer_url || ""}
                  onChange={(e) => setEditing({ ...editing, config: { ...editing.config, issuer_url: e.target.value } })}
                  className="w-full border border-border px-3 py-2 text-sm font-mono-mini"
                  placeholder="https://accounts.google.com"
                  data-testid="sso-edit-issuer"
                />
                <div className="text-[10px] text-muted-foreground mt-1">
                  We&apos;ll auto-discover the OIDC endpoints from {`{issuer_url}/.well-known/openid-configuration`}.
                </div>
              </div>

              <div>
                <label className="eyebrow block mb-1">Client ID *</label>
                <input
                  type="text"
                  value={editing.config?.client_id || ""}
                  onChange={(e) => setEditing({ ...editing, config: { ...editing.config, client_id: e.target.value } })}
                  className="w-full border border-border px-3 py-2 text-sm font-mono-mini"
                  data-testid="sso-edit-client-id"
                />
              </div>

              <div>
                <label className="eyebrow block mb-1">
                  Client Secret {!editing._new && <span className="normal-case font-mono-mini text-muted-foreground">(leave blank to keep unchanged)</span>}
                </label>
                <input
                  type="password"
                  value={editing.config?.client_secret === "***" ? "" : (editing.config?.client_secret || "")}
                  onChange={(e) => setEditing({ ...editing, config: { ...editing.config, client_secret: e.target.value } })}
                  className="w-full border border-border px-3 py-2 text-sm font-mono-mini"
                  placeholder={editing._new ? "Paste from IdP" : "•••••••• (unchanged)"}
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="eyebrow block mb-1">Scopes</label>
                  <input
                    type="text"
                    value={Array.isArray(editing.config?.scopes) ? editing.config.scopes.join(" ") : (editing.config?.scopes || "")}
                    onChange={(e) => setEditing({ ...editing, config: { ...editing.config, scopes: e.target.value } })}
                    className="w-full border border-border px-3 py-2 text-sm font-mono-mini"
                    placeholder="openid email profile"
                  />
                </div>
                <div>
                  <label className="eyebrow block mb-1">Default role for new users</label>
                  <select
                    value={editing.config?.default_role || "employee"}
                    onChange={(e) => setEditing({ ...editing, config: { ...editing.config, default_role: e.target.value } })}
                    className="w-full border border-border px-3 py-2 text-sm"
                  >
                    <option value="employee">Employee</option>
                    <option value="manager">Manager</option>
                    <option value="hr">HR</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="eyebrow block mb-1">Allowed email domains (optional)</label>
                <input
                  type="text"
                  value={Array.isArray(editing.config?.allowed_domains) ? editing.config.allowed_domains.join(", ") : (editing.config?.allowed_domains || "")}
                  onChange={(e) => setEditing({ ...editing, config: { ...editing.config, allowed_domains: e.target.value } })}
                  className="w-full border border-border px-3 py-2 text-sm font-mono-mini"
                  placeholder="company.com, partner.io"
                />
                <div className="text-[10px] text-muted-foreground mt-1">
                  Comma or space separated. Leave empty to allow any email domain from this provider.
                </div>
              </div>

              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!editing.enabled}
                  onChange={(e) => setEditing({ ...editing, enabled: e.target.checked })}
                  className="w-4 h-4"
                  data-testid="sso-edit-enabled"
                />
                <span className="text-sm font-semibold">Enabled (show on login page)</span>
              </label>
            </div>
            <div className="border-t border-border p-4 flex items-center justify-end gap-2">
              <button onClick={() => setEditing(null)} className="border border-border px-3 py-2 text-sm hover:bg-muted">Cancel</button>
              <button
                onClick={save}
                disabled={!editing.key || !editing.label || !editing.config?.issuer_url || !editing.config?.client_id}
                className="btn-primary flex items-center gap-2 disabled:opacity-40"
                data-testid="sso-edit-save"
              >
                <Plus size={12} weight="bold" /> {editing._new ? "Create provider" : "Save changes"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
